import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { useToast } from "@/hooks/use-toast";
import { useRegisterForm } from "./hooks/useRegisterForm";
import { useTranslation } from "react-i18next";
import { PersonalInfoFields } from "./register/PersonalInfoFields";
import { AuthFields } from "./register/AuthFields";
import { TermsAgreement } from "./register/TermsAgreement";
import { Form } from "@/components/ui/form"; // Import Form component

interface RegisterFormProps {
  onAuthComplete?: () => void;
  isStaffInvite?: boolean;
  defaultEmail?: string | null;
}

export function RegisterForm({
  onAuthComplete,
  isStaffInvite = false,
  defaultEmail = null,
}: RegisterFormProps) {
  const { t } = useTranslation();

  // Use the custom hook with proper params
  const { form, isLoading, isValid, onSubmit } = useRegisterForm(
    isStaffInvite,
    onAuthComplete,
    defaultEmail
  );

  return (
    <Card className="w-full max-w-md">
      <CardHeader>
        <CardTitle>{t("register")}</CardTitle>
        <CardDescription>{t("createAccount")}</CardDescription>
      </CardHeader>
      <CardContent>
        {/* Wrap the entire form content with the Form component from shadcn-ui */}
        <Form {...form}>
          <form onSubmit={onSubmit} className="space-y-4">
            <PersonalInfoFields form={form} />
            <AuthFields form={form} defaultEmail={defaultEmail} />
            <TermsAgreement form={form} />

            <Button
              type="submit"
              className="w-full"
              disabled={isLoading || !form.formState.isValid}
            >
              {isLoading ? t("registering") : t("register")}
            </Button>
          </form>
        </Form>
      </CardContent>
      <CardFooter className="flex justify-center">
        <p className="text-sm text-muted-foreground">
          {t("alreadyHaveAccount")}{" "}
          <Link to="/login" className="text-primary hover:underline">
            {t("login")}
          </Link>
        </p>
      </CardFooter>
    </Card>
  );
}
